// Copyright (c) Microsoft Corporation.
// Licensed under the MIT License.

import { constraints, getUniqueId, QuestionGroup, QuestionOption, SerializationContext } from '.';
import { IMessageSink, isNullOrEmptyString, isObjectType } from '../utilities';
import { questionType } from '../validation';
import { PropertyBag } from './property-bag';

/**
 * Type of a question in a 'check your understanding' quiz.
 */
export enum QuestionType {
    Choice = 'choice',
    Multiple = 'multiple',
    String = 'string',
    Mapping = 'mapping',
    Reflection = 'reflection',
}

/**
 * Question inside a 'check your understanding' section.
 */
export class Question<TLocation = string> {
    private readonly props: PropertyBag<TLocation>;
    private _options: QuestionOption<TLocation>[] = [];

    /** @internal */
    public readonly parent: QuestionGroup<TLocation>;

    /** @internal */
    /** Creates a new instance of the {@link Question} class. */
    public constructor(parent: QuestionGroup<TLocation>, id?: string) {
        this.parent = parent;
        this.props = new PropertyBag<TLocation>(parent.owner, {
            id: id ?? '',
            type: QuestionType.String,
            content: '',
            scrambleOrder: false,
        });
    }

    /** @internal */
    /** Deserializes this object from specified context. */
    public deserializeFrom(context: SerializationContext<TLocation>) {
        this.id = context.getString('id') ?? '';

        this.type = (context.getString('type') ?? QuestionType.String) as QuestionType;
        this.content = context.getString('content') ?? '';
        this.hint = context.getString('hint', true);
        this.scrambleOrder = context.getBoolean('scrambleOrder') ?? false;
        context.getArray('options')?.forEach((item) => {
            if (!isObjectType(item)) {
                context.warning('options', 'Invalid question option will be ignored.');
            } else {
                const option = new QuestionOption(this);
                option.deserializeFrom(context.createNested(item));
                if (isNullOrEmptyString(option.id) || this._options.some((o) => o.id === option.id)) {
                    context.warning('options', 'Question option with duplicate or missing ID will be ignored.');
                } else {
                    this._options.push(option);
                }
            }
        });

        context.finalize();
    }

    /** @internal */
    /** Serializes this object to specified context. */
    public serialize(context: SerializationContext<TLocation>) {
        context.putValue('id', this.id);
        context.putValue('type', this.type);
        context.putValue('content', this.content);
        context.putNullableString('hint', this.hint);
        if (this.scrambleOrder !== false) {
            context.putValue('scrambleOrder', this.scrambleOrder);
        }

        context.putArray(
            'options',
            this._options.map((option) => {
                const nested = context.createNested();
                option.serialize(nested);
                return nested.object;
            })
        );
    }

    /** Returns ID of the question unique with the group. */
    public get id(): string {
        return this.props.get('id');
    }
    private set id(value: string) {
        this.props.set('id', value);
    }

    /** Gets or sets type of the question. */
    public get type(): QuestionType {
        return this.props.get('type');
    }
    public set type(value: QuestionType) {
        this.props.set('type', value);
    }

    /** Gets or sets content of the question. */
    public get content(): string {
        return this.props.get('content');
    }
    public set content(value: string) {
        this.props.set('content', value);
    }

    /** Gets or sets hint for the question. */
    public get hint(): string | undefined {
        return this.props.get('hint');
    }
    public set hint(value: string | undefined) {
        this.props.set('hint', value);
    }

    /** Gets or sets whether the order of answer options should be scrambled. */
    public get scrambleOrder(): boolean {
        return this.props.get('scrambleOrder');
    }
    public set scrambleOrder(value: boolean) {
        this.props.set('scrambleOrder', value);
    }

    /** Returns list of answer options. */
    public get options(): ReadonlyArray<QuestionOption<TLocation>> {
        return this._options;
    }

    /** Returns list of correct answer options. */
    public get correctOptions(): ReadonlyArray<QuestionOption<TLocation>> {
        return this._options.filter((o) => o.isCorrect);
    }

    /** Adds a new option to the question and returns it. */
    public addOption(): QuestionOption<TLocation> {
        const question = new QuestionOption(this, getUniqueId(this._options));
        this._options.push(question);
        this.parent.owner.fireHierarchyChanged();
        return question;
    }

    /** Removes existing option (by reference or ID) from the question. */
    public removeOption(option: string | QuestionOption<TLocation>): boolean {
        const id = typeof option === 'string' ? option : option.id;
        const index = this._options.findIndex((o) => o.id === id);
        if (index >= 0) {
            this._options.splice(index, 1);
            this.parent.owner.fireHierarchyChanged();
            return true;
        } else {
            return false;
        }
    }

    /** Validates the question. */
    public validate(messages: IMessageSink<TLocation>, _recursive?: boolean): void {
        constraints.enum.validate(this, 'type', questionType, messages);
        constraints.description.validate(this, 'content', messages);
        constraints.description.validate(this, 'hint', messages);
        this._options.forEach((o) => o.validate(messages));
    }

    /** Returns string representation of the question. */
    public toString() {
        return `question ${this.id}`;
    }
}
